import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { ArrowLeft, ArrowRight, Clock, Check, Heart, MessageCircle, X, Send, ClipboardCheck, ChevronDown, Gift, ShoppingBag, Users, AlertTriangle, Sparkles, CreditCard } from "lucide-react";
import { format, addDays, isBefore, startOfToday } from "date-fns";
import ConsultationForm from "@/components/ConsultationForm";
import BookingStep1Services from "@/components/booking/BookingStep1Services";
import BookingStep5Confirm from "@/components/booking/BookingStep5Confirm";
import { getTenantSlugFromPath } from "@/lib/tenant";

export default function BookingWizard() {
  // Path-based multi-tenancy: tenant slug is the first URL segment, not a
  // route param. Falls back to the legacy /book/:salonSlug param if present.
  const params = useParams();
  const salonSlug = getTenantSlugFromPath();
  // Unused but kept silent: legacy params (avoid lint warning on unused destructure).
  void params;
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [settings, setSettings] = useState({});
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  // Group booking
  const [additionalGuests, setAdditionalGuests] = useState([]);
  const [splitType, setSplitType] = useState('bulk'); // 'bulk' | 'split'
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [policyAck, setPolicyAck] = useState(false);
  const [booking, setBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paymentVerifying, setPaymentVerifying] = useState(false);

  // Detect return from Stripe checkout (session_id in URL)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const pendingId = params.get('pending_id');
    const cancelled = params.get('payment_cancelled');
    if (sessionId) {
      setPaymentVerifying(true);
      window.history.replaceState({}, '', window.location.pathname);
      const poll = async (attempt = 0) => {
        if (attempt >= 15) { setPaymentVerifying(false); toast.error("Could not verify payment. Please contact us."); return; }
        try {
          const url = `${API}/payments/status/${sessionId}${pendingId ? `?pending_id=${encodeURIComponent(pendingId)}` : ''}`;
          const res = await axios.get(url);
          if (res.data.payment_status === 'paid') {
            setPaymentVerifying(false);
            setBooking({ success: true, appointment: { id: res.data.booking_id } });
            setStep(6);
            toast.success("Deposit paid — your booking is confirmed!");
            return;
          }
        } catch {}
        setTimeout(() => poll(attempt + 1), 2500);
      };
      poll();
    } else if (cancelled) {
      toast.error("Payment cancelled — your booking was not completed.");
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Consultation form + new client discount
  const [isNewClient, setIsNewClient] = useState(null);
  const [newClientDiscount, setNewClientDiscount] = useState(0);
  const [consultationDone, setConsultationDone] = useState(false);
  // AI Assistant
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  // Voucher
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherApplied, setVoucherApplied] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  // VIP recognition
  const [clientStatus, setClientStatus] = useState(null); // {is_vip, completed_bookings, bookings_until_vip, ...}
  const [eligibilityBlock, setEligibilityBlock] = useState(null);
  // Add-on selections: { [serviceId]: { length: "Medium", lengthUplift: 5, extras: [{name, price}] } }
  const [addOnSelections, setAddOnSelections] = useState({});

  // Compute services whose eligibility window matches the client's days-since-last-visit.
  // Only returns eligibility-gated services (general services without a window are excluded
  // to avoid noise — they're always bookable and don't need a proactive nudge).
  const getRecommendedServices = (days, svcs) => {
    if (days == null || days === undefined) return [];
    return svcs.filter(s => {
      if (!s.active) return false;
      const min = parseInt(s.eligibility_min_days_since_last) || 0;
      const max = parseInt(s.eligibility_max_days_since_last) || 0;
      if (!min && !max) return false; // no eligibility constraint → not a cadence-based service
      if (min && days < min) return false;
      if (max && days > max) return false;
      return true;
    });
  };

  const recommendedServices = getRecommendedServices(
    clientStatus?.days_since_last_appointment,
    services
  );

  useEffect(() => {
    const load = async () => {
      setServicesLoading(true);
      setServicesError(null);
      try {
        const [svcRes, staffRes, setRes] = await Promise.all([
          axios.get(`${API}/services?active_only=true`),
          axios.get(`${API}/staff?active_only=true&slim=true`),
          axios.get(`${API}/settings?lite=true`)
        ]);
        setServices(svcRes.data);
        setStaff(staffRes.data);
        setSettings(setRes.data);
        // Set default category to first in order
        const rawCats = [...new Set(svcRes.data.map(s => s.category || 'Other'))];
        const order = setRes.data.category_order || [];
        const orderedCats = [
          ...order.filter(c => rawCats.includes(c)),
          ...rawCats.filter(c => !order.includes(c))
        ];
        if (orderedCats.length > 0) setSelectedCategory(orderedCats[0]);
      } catch (e) {
        setServicesError(e?.message || "Failed to load services. Please refresh.");
      } finally {
        setServicesLoading(false);
      }
    };
    load();
  }, [salonSlug]);

  // Pre-fill from rebook CTA links (Thank You email).
  // Reads ?rebook_date=YYYY-MM-DD&name=...&email=...&phone=...&service_id=...
  // Pre-fills client info immediately and, once services are loaded, selects
  // the matching service and target date so the user jumps straight to slot
  // picking on their preferred rebook date.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const name  = sp.get('name');
    const email = sp.get('email');
    const phone = sp.get('phone');
    if (name)  setClientName(name);
    if (email) setClientEmail(email);
    if (phone) setClientPhone(phone);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once services have loaded, honour the ?service_id and ?rebook_date params.
  // Only auto-advances when the matching service exists; otherwise the wizard
  // starts fresh on step 1 so the client can still pick something.
  useEffect(() => {
    if (servicesLoading || !services.length) return;
    const sp = new URLSearchParams(window.location.search);
    const svcId = sp.get('service_id');
    const rebookDate = sp.get('rebook_date');
    if (!svcId && !rebookDate) return;
    // Don't re-apply if user has already started interacting
    if (selectedServices.length > 0) return;
    if (svcId) {
      const svc = services.find(s => s.id === svcId && s.active);
      if (svc) {
        setSelectedServices([svc]);
        setSelectedCategory(svc.category || 'Other');
      }
    }
    if (rebookDate) {
      const d = new Date(rebookDate + 'T00:00:00');
      if (!isNaN(d.getTime()) && !isBefore(d, startOfToday())) {
        setSelectedDate(d);
        // Date/Time is step 4 in the new [Details → Services → Date/Time →
        // Confirm] order — jump there if we have a service pre-selected so
        // the client lands directly on time slots. Since the Details step
        // (which normally fetches clientStatus) is being skipped, fetch it
        // directly here so VIP/discount/nail-length data is still available.
        if (svcId && services.some(s => s.id === svcId)) {
          setStep(4);
          if (clientEmail) {
            axios.get(`${API}/check-new-client?email=${encodeURIComponent(clientEmail)}`)
              .then(res => { setClientStatus(res.data); setIsNewClient(res.data.is_new); })
              .catch(() => {});
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, servicesLoading]);

  // Calculate total with add-ons
  const getServiceTotal = (svc) => {
    const sel = addOnSelections[svc.id] || {};
    const lengthUplift = sel.lengthUplift || 0;
    const extrasTotal = (sel.extras || []).reduce((s, e) => s + (e.price || 0), 0);
    return svc.price + lengthUplift + extrasTotal;
  };
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration_minutes, 0);
  const totalPrice = selectedServices.reduce((sum, s) => sum + getServiceTotal(s), 0);

  // Compute the deposit the client will be charged (used on Step 5 UI)
  const stripeDepositAmount = (() => {
    if (!settings?.stripe_deposit_enabled) return 0;
    if (clientStatus?.cash_only) return 0;   // Trusted/loyal client — no deposit required
    const amt = parseFloat(settings?.deposit_amount || 20);
    if (settings?.deposit_type === 'percent') return Math.max(0.50, Math.round(totalPrice * amt) / 100);
    return Math.max(0.50, amt);
  })();

  // Auto-apply best available VIP reward when reaching the confirm step.
  // Runs once we know the client status and are showing the booking summary.
  useEffect(() => {
    if (step !== 5) return;
    if (voucherApplied) return;                                  // user already picked one
    const rewards = clientStatus?.available_rewards || [];
    if (rewards.length === 0 || selectedServices.length === 0) return;

    const cartServices = selectedServices.map(s => ({
      service_id: s.id,
      name: s.name,
      price: getServiceTotal(s),
    }));
    const effectiveTotal = Math.max(0, totalPrice - newClientDiscount);

    let cancelled = false;
    (async () => {
      // Validate each reward in order; pick the highest value that's actually applicable
      let best = null;
      for (const r of rewards) {
        try {
          const res = await axios.post(`${API}/vouchers/validate`, {
            code: r.code,
            total: effectiveTotal,
            services: cartServices,
          });
          if (res.data.valid && (!best || (res.data.amount_to_apply || 0) > (best.amount_to_apply || 0))) {
            best = res.data;
          }
        } catch (e) { /* ignore individual failures */ }
      }
      if (!cancelled && best) {
        setVoucherApplied(best);
        setVoucherCode(best.code);
        toast.success(`VIP reward auto-applied: ${best.code}`);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, clientStatus]);

  // Group booking: check if any selected service has group_size > 1
  const groupService = selectedServices.find(s => (s.group_size || 1) > 1);
  const groupSize = groupService ? (groupService.group_size || 1) : 1;
  const isGroupBooking = groupSize > 1;
  const guestsNeeded = Math.max(0, groupSize - 1);

  // Per-person price for split calculation
  const splitAmount = isGroupBooking && splitType === 'split' ? (totalPrice / groupSize) : totalPrice;

  const toggleService = (svc) => {
    const alreadySelected = !!selectedServices.find(s => s.id === svc.id);
    setSelectedServices(prev => {
      const exists = prev.find(s => s.id === svc.id);
      const newList = exists ? prev.filter(s => s.id !== svc.id) : [...prev, svc];
      // Reset group guests when services change
      const newGroupSvc = newList.find(s => (s.group_size || 1) > 1);
      const newGroupSize = newGroupSvc ? (newGroupSvc.group_size || 1) : 1;
      const newGuestsNeeded = Math.max(0, newGroupSize - 1);
      setAdditionalGuests(Array.from({ length: newGuestsNeeded }, (_, i) => additionalGuests[i] || { name: '', email: '', phone: '' }));
      return newList;
    });
    // Default nail length to the client's verified last-visit length when
    // adding a length-enabled service — encourages accurate self-reporting
    // by starting from what we already know is true.
    if (!alreadySelected && svc.length_pricing_enabled) {
      const verified = clientStatus?.last_nail_length;
      const master = settings.nail_lengths || [];
      const verifiedOpt = verified ? master.find(o => o.name === verified) : null;
      if (verifiedOpt && !addOnSelections[svc.id]?.length) {
        setAddOnSelections(prev => ({
          ...prev,
          [svc.id]: { ...prev[svc.id], length: verifiedOpt.name, lengthUplift: verifiedOpt.price_uplift || 0 },
        }));
      }
    }
  };

  // Nail-length "honesty" check — a client's selection is compared against
  // the salon-verified length from their last visit (client.verified_nail_length,
  // surfaced via /check-new-client as clientStatus.last_nail_length). Picking
  // something CHEAPER than the verified truth blocks with a confirmation
  // modal (the actual revenue-loss case); picking something MORE EXPENSIVE
  // just gets a friendly non-blocking toast.
  const [lengthMismatch, setLengthMismatch] = useState(null);
  const applyLength = (svc, opt) => setAddOnSelections(prev => ({
    ...prev,
    [svc.id]: { ...prev[svc.id], length: opt.name, lengthUplift: opt.price_uplift || 0 },
  }));
  const handleSelectLength = (svc, opt) => {
    const verified = clientStatus?.last_nail_length;
    if (!verified || clientStatus?.is_new || verified === opt.name) {
      applyLength(svc, opt);
      return;
    }
    const master = settings.nail_lengths || [];
    const verifiedOpt = master.find(o => o.name === verified);
    if (!verifiedOpt) { applyLength(svc, opt); return; }
    const verifiedUplift = verifiedOpt.price_uplift || 0;
    const selectedUplift = opt.price_uplift || 0;
    if (selectedUplift < verifiedUplift) {
      setLengthMismatch({ svc, opt, verifiedOpt });
    } else if (selectedUplift > verifiedUplift) {
      applyLength(svc, opt);
      toast(`Just checking — last time you had ${verified}, this time ${opt.name}?`);
    } else {
      applyLength(svc, opt);
    }
  };

  const fetchSlots = async (date) => {
    if (!date) return;
    setLoadingSlots(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const params = new URLSearchParams({ date: dateStr, duration_minutes: totalDuration.toString() });
      if (selectedStaff) params.append('staff_id', selectedStaff);
      const res = await axios.get(`${API}/availability?${params}`);
      setAvailableSlots(res.data.slots || []);
    } catch (e) {
      
      setAvailableSlots([]);
    }
    setLoadingSlots(false);
  };

  useEffect(() => {
    if (step === 4 && selectedDate) {
      fetchSlots(selectedDate);
    }
  }, [selectedDate, selectedStaff, step]);

  const handleBook = async () => {
    if (!clientName || !clientEmail) {
      toast.error("Please fill in your name and email");
      return;
    }
    // Validate group guests if group booking
    if (isGroupBooking) {
      const missing = additionalGuests.filter(g => !g.name.trim());
      if (missing.length > 0) {
        toast.error("Please enter a name for each additional guest");
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        salon_slug: salonSlug,
        services: selectedServices.map(s => {
          const sel = addOnSelections[s.id] || {};
          return {
            service_id: s.id,
            name: s.name,
            duration_minutes: s.duration_minutes,
            price: s.price,
            selected_length: sel.length || null,
            length_uplift: sel.lengthUplift || 0,
            selected_extras: sel.extras || [],
          };
        }),
        staff_id: selectedStaff || (selectedSlot?.staff_id || null),
        appointment_datetime: selectedSlot?.datetime,
        client_name: clientName,
        client_email: clientEmail,
        client_phone: clientPhone,
        notes,
        policy_acknowledged: policyAck,
        policy_acknowledged_at: new Date().toISOString(),
        // Group booking
        additional_guests: isGroupBooking ? additionalGuests : [],
        split_type: isGroupBooking ? splitType : 'bulk',
        group_size: groupSize
      });
      if (res.data.success) {
        // Redeem voucher / promo code if applied
        if (voucherApplied) {
          try {
            const redeemUrl = voucherApplied.source === 'promo_code' ? 'promo-codes/redeem' : 'vouchers/redeem';
            await axios.post(`${API}/${redeemUrl}`, {
              code: voucherApplied.code,
              appointment_id: res.data.appointment?.id,
              amount: voucherApplied.amount_to_apply
            });
          } catch (ve) {  }
        }
        setBooking(res.data);
        setStep(6);
        toast.success("Booking confirmed!");
      }
    } catch (e) {
      toast.error("Failed to create booking. Please try again.");
    }
    setSubmitting(false);
  };

  /** Stripe payment path: store booking as pending → redirect to Stripe → booking created on payment.
   *  paymentChoice: "deposit" (default) or "full" — full charges the entire total upfront. */
  const handleStripeBook = async (paymentChoice = "deposit") => {
    if (!clientName || !clientEmail) { toast.error("Please fill in your name and email"); return; }
    setSubmitting(true);
    try {
      const finalTotal = Math.max(0, totalPrice - newClientDiscount - (voucherApplied?.amount_to_apply || 0));
      const bookingPayload = {
        salon_slug: salonSlug,
        services: selectedServices.map(s => {
          const sel = addOnSelections[s.id] || {};
          return { service_id: s.id, name: s.name, duration_minutes: s.duration_minutes, price: s.price, selected_length: sel.length || null, length_uplift: sel.lengthUplift || 0, selected_extras: sel.extras || [] };
        }),
        staff_id: selectedStaff || (selectedSlot?.staff_id || null),
        appointment_datetime: selectedSlot?.datetime,
        client_name: clientName, client_email: clientEmail, client_phone: clientPhone,
        notes, policy_acknowledged: true, policy_acknowledged_at: new Date().toISOString(),
        additional_guests: isGroupBooking ? additionalGuests : [], split_type: isGroupBooking ? splitType : 'bulk', group_size: groupSize,
        estimated_total: finalTotal,
      };
      const res = await axios.post(`${API}/payments/initiate`, {
        origin_url: window.location.origin,
        booking_slug: salonSlug,
        booking_data: bookingPayload,
        payment_choice: paymentChoice,
        voucher_code: voucherApplied?.code || null,
        voucher_amount: voucherApplied?.amount_to_apply || 0,
        code_source: voucherApplied?.source === 'promo_code' ? 'promo' : 'voucher',
      });
      window.location.href = res.data.checkout_url;
      return;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start payment. Please try again.");
    }
    setSubmitting(false);
  };

  const handleDetailsNext = async () => {
    if (!clientName || !clientEmail) {
      toast.error("Please fill in your name and email");
      return;
    }
    // Check if client is new (for consultation form + discount + VIP + nail-length honesty)
    try {
      const [formRes, newRes] = await Promise.all([
        axios.get(`${API}/consultation-forms?client_email=${encodeURIComponent(clientEmail)}`),
        axios.get(`${API}/check-new-client?email=${encodeURIComponent(clientEmail)}`)
      ]);
      const hasForm = (formRes.data.forms || []).length > 0;
      const clientIsNew = newRes.data.is_new;
      const discount = newRes.data.discount || 0;

      setIsNewClient(clientIsNew);
      setNewClientDiscount(discount);
      setClientStatus(newRes.data);  // Store full VIP + last-service + nail-length info

      if (hasForm) {
        setConsultationDone(true);
        setStep(3); // Straight to services
      } else {
        setConsultationDone(false);
        setStep(2); // Show consultation form
      }
    } catch (e) {
      setIsNewClient(true);
      setNewClientDiscount(5);
      setConsultationDone(false);
      setStep(2);
    }
  };

  // Runs when leaving the Services step — checks eligibility windows against
  // the clientStatus we already fetched on the Details step (no network
  // call needed here since we identify the client first now).
  const handleServicesNext = () => {
    const daysSinceLast = clientStatus?.days_since_last_appointment;
    const blocking = (selectedServices || []).filter(s => {
      const minD = parseInt(s.eligibility_min_days_since_last) || 0;
      const maxD = parseInt(s.eligibility_max_days_since_last) || 0;
      if (!minD && !maxD) return false;
      if (daysSinceLast === null || daysSinceLast === undefined) return true; // first-timer blocked
      if (minD && daysSinceLast < minD) return true;
      if (maxD && daysSinceLast > maxD) return true;
      return false;
    });
    if (blocking.length > 0) {
      // Find suggested alternatives: any active service WITHOUT eligibility OR whose window matches
      const suggestions = (services || []).filter(s => {
        if (blocking.find(b => b.id === s.id)) return false;
        if (!s.active) return false;
        const minD = parseInt(s.eligibility_min_days_since_last) || 0;
        const maxD = parseInt(s.eligibility_max_days_since_last) || 0;
        if (!minD && !maxD) return true;
        if (daysSinceLast === null || daysSinceLast === undefined) return false;
        if (minD && daysSinceLast < minD) return false;
        if (maxD && daysSinceLast > maxD) return false;
        return true;
      }).slice(0, 5);
      setEligibilityBlock({
        blocked: blocking,
        suggestions,
        daysSinceLast,
        isFirstTime: daysSinceLast === null || daysSinceLast === undefined,
      });
      return; // Stop progression — modal will handle it
    }
    setStep(4); // Date & time
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatInput("");
    setChatLoading(true);
    try {
      const res = await axios.post(`${API}/ai/booking-assistant`, {
        message: userMsg,
        session_id: chatSessionId
      });
      setChatSessionId(res.data.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I'm having trouble. Please try again!" }]);
    }
    setChatLoading(false);
  };

  if (paymentVerifying) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" style={{ background: settings?.color_background || '#faf7f6' }}>
        <img src="https://customer-assets.emergentagent.com/job_a2ffd257-50b9-4530-8932-b0e03417ee6f/artifacts/9v3936lg_Beyond%20AI.png" alt="" className="w-16 h-16 rounded-2xl object-cover mb-5 opacity-80" />
        <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display', color: settings?.color_text || '#3d3433' }}>Confirming your payment…</h2>
        <p className="text-sm text-muted-foreground mb-6">Please wait, this only takes a moment.</p>
        <div className="flex gap-1.5">
          {[0, 150, 300].map(delay => (
            <div key={delay} className="w-2.5 h-2.5 rounded-full animate-bounce" style={{ background: settings?.color_accent || '#c9a89c', animationDelay: `${delay}ms` }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" />
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-sm border-b border-border">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            {(settings.icon_logo_url || settings.logo_url) ? (
              <img src={settings.icon_logo_url || settings.logo_url} alt={settings.salon_name || 'Salon'} className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                {((settings.salon_name || 'B')[0]).toUpperCase()}
              </div>
            )}
            <span className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>{settings.salon_name || ''}</span>
          </Link>
          {step < 6 && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground mr-2">Step {Math.min(step, 5)}/{isNewClient === false ? 4 : 5}</span>
              <div className="step-indicator">
                {(isNewClient === false ? [1,3,4,5] : [1,2,3,4,5]).map(s => <div key={s} className={`step-dot ${s <= step ? 'active' : ''}`} />)}
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Beyond AI Banner */}
        {step === 3 && !chatOpen && (
          <button
            onClick={() => setChatOpen(true)}
            data-testid="ai-assistant-open-button"
            className="w-full flex items-center gap-3 p-4 mb-6 rounded-2xl hover:shadow-md transition-shadow duration-200 border"
            style={{ background: 'rgba(201,168,156,0.12)', borderColor: 'rgba(201,168,156,0.40)' }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_a2ffd257-50b9-4530-8932-b0e03417ee6f/artifacts/9v3936lg_Beyond%20AI.png"
              alt="Beyond AI"
              className="w-12 h-12 rounded-2xl object-cover shrink-0"
            />
            <div className="text-left flex-1">
              <p className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display', color: '#3d3433' }}>
                Not sure what to book? <span style={{ color: '#c9a89c' }}>Ask Beyond AI</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">It knows all our services, prices, and can recommend the perfect treatment</p>
            </div>
            <ArrowRight size={18} style={{ color: '#c9a89c' }} className="shrink-0" />
          </button>
        )}

        {/* Step 3: Select Services */}
        {step === 3 && (
          <BookingStep1Services
            services={services}
            settings={settings}
            selectedServices={selectedServices}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            toggleService={toggleService}
            addOnSelections={addOnSelections}
            setAddOnSelections={setAddOnSelections}
            onSelectLength={handleSelectLength}
            getServiceTotal={getServiceTotal}
            onNext={handleServicesNext}
            recommendedServices={recommendedServices}
            clientStatus={clientStatus}
            loading={servicesLoading}
            error={servicesError}
            onRetry={() => window.location.reload()}
          />
        )}


        {/* Step 4: Date & Time */}
        {step === 4 && (
          <div className="fade-in">
            <h2 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: 'Playfair Display' }}>Pick a date & time</h2>
            <p className="text-sm text-muted-foreground mb-6">{totalDuration} minutes total</p>
            
            {staff.length > 0 && (
              <div className="mb-6">
                <Label className="text-sm font-medium mb-2 block">Preferred stylist (optional)</Label>
                <div className="flex flex-wrap gap-2">
                  <button 
                    onClick={() => setSelectedStaff(null)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${!selectedStaff ? 'bg-primary text-white' : 'bg-secondary text-secondary-foreground hover:bg-muted'}`}
                  >Any available</button>
                  {staff.map(s => (
                    <button 
                      key={s.id}
                      onClick={() => setSelectedStaff(s.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedStaff === s.id ? 'bg-primary text-white' : 'bg-secondary text-secondary-foreground hover:bg-muted'}`}
                    >{s.name}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-medium mb-2 block">Select date</Label>
                <Card className="p-3 rounded-2xl inline-block">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    defaultMonth={selectedDate || undefined}
                    onSelect={(d) => { setSelectedDate(d); setSelectedSlot(null); }}
                    disabled={(date) => isBefore(date, startOfToday())}
                    className="rounded-xl"
                  />
                </Card>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {selectedDate ? `Available times on ${format(selectedDate, 'EEE, d MMM')}` : 'Select a date first'}
                </Label>
                {loadingSlots ? (
                  <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-muted rounded-xl animate-pulse" />)}</div>
                ) : (
                  <div className="slot-grid">
                    {availableSlots.map((slot, i) => (
                      <button
                        key={i}
                        data-testid={`time-slot-${slot.time}`}
                        onClick={() => setSelectedSlot(slot)}
                        className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-150 ${
                          selectedSlot?.time === slot.time && selectedSlot?.staff_id === slot.staff_id
                            ? 'bg-primary text-white' 
                            : 'bg-secondary hover:bg-primary/10 text-secondary-foreground'
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                    {selectedDate && !loadingSlots && availableSlots.length === 0 && (
                      <p className="text-sm text-muted-foreground col-span-full py-4">No available slots on this date. Try another day.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Details */}
        {step === 1 && (
          <div className="fade-in">
            <h2 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: 'Playfair Display' }}>Your details</h2>
            <p className="text-sm text-muted-foreground mb-6">We'll send your confirmation here</p>
            <div className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="name">Full name *</Label>
                <Input id="name" data-testid="booking-name-input" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Your name" className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" data-testid="booking-email-input" type="email" value={clientEmail}
                  onChange={e => setClientEmail(e.target.value)}
                  onBlur={async () => {
                    if (clientEmail && clientEmail.includes('@')) {
                      try {
                        const res = await axios.get(`${API}/check-new-client?email=${encodeURIComponent(clientEmail)}`);
                        setClientStatus(res.data);
                      } catch {}
                    }
                  }}
                  placeholder="your@email.com" className="mt-1 rounded-xl" />
              </div>

              {/* ── VIP / Loyalty Callout ── */}
              {clientStatus && !clientStatus.is_new && (
                <div data-testid="vip-status-message" className={`relative overflow-hidden rounded-2xl p-5 ${
                  clientStatus.is_vip
                    ? 'bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-200/60'
                    : 'bg-gradient-to-br from-rose-50 via-pink-50 to-fuchsia-50 border-2 border-pink-200/60'
                }`}>
                  {/* Decorative shimmer */}
                  <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl opacity-30 ${
                    clientStatus.is_vip ? 'bg-amber-300' : 'bg-pink-300'
                  }`} />
                  {clientStatus.is_vip ? (
                    <div className="relative">
                      <p className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>
                        <span className="mr-1.5">&#11088;</span>
                        {(clientStatus.labels?.vip_headline || "You're a VIP client")}
                      </p>
                      <p className="text-sm mt-1.5 text-amber-800/80">
                        <span className="mr-1.5">&#127873;</span>
                        {(clientStatus.labels?.vip_subtext || '{remaining} visits until your next reward')
                          .replace('{remaining}', clientStatus.visits_until_reward)}
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      <p className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>
                        <span className="mr-1.5">&#128133;</span>
                        {(clientStatus.labels?.pre_vip_headline || '{remaining} more visits until VIP')
                          .replace('{remaining}', clientStatus.visits_until_vip)}
                      </p>
                      <p className="text-sm mt-1.5 text-pink-800/70">
                        <span className="mr-1.5">&#127775;</span>
                        {(clientStatus.labels?.pre_vip_subtext || 'VIP clients get rewards every {reward_interval} visits + exclusive perks')
                          .replace('{reward_interval}', clientStatus.reward_interval)}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {clientStatus && clientStatus.is_new && clientStatus.discount > 0 && (
                <div data-testid="new-client-message" className="rounded-2xl p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200/60">
                  <p className="text-sm font-semibold text-green-800">
                    <span className="mr-1.5">&#127881;</span>
                    Welcome! You'll receive a <strong>£{clientStatus.discount}</strong> new client discount.
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input id="phone" data-testid="booking-phone-input" value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="07xxx xxx xxx" className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any special requests or info" className="mt-1 rounded-xl" rows={3} />
              </div>
              <div className={`flex items-start gap-2 p-3 rounded-xl border-2 transition-colors ${policyAck ? 'border-primary/30 bg-primary/5' : 'border-border'}`}>
                <Checkbox id="policy" checked={policyAck} onCheckedChange={setPolicyAck} className="mt-0.5" />
                <label htmlFor="policy" className="text-xs leading-tight cursor-pointer">
                  <span className="font-medium text-foreground">I acknowledge the cancellation policy</span>
                  <span className="text-muted-foreground block mt-0.5">
                    Cancellations within {settings.cancellation_window_hours || 24} hours of the appointment will be charged in full.
                  </span>
                </label>
              </div>
            </div>

            {/* ── Group Booking: Additional Guests ── */}
            {isGroupBooking && (
              <div className="mt-8 max-w-xl">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center">
                    <Users size={15} className="text-purple-700" />
                  </div>
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display' }}>
                    Group Booking — {groupSize} people
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-5">
                  Please enter the details of the other {guestsNeeded} {guestsNeeded === 1 ? 'person' : 'people'} in your group.
                </p>

                <div className="space-y-4">
                  {Array.from({ length: guestsNeeded }).map((_, i) => (
                    <div key={i} className="p-4 rounded-2xl border bg-muted/10 space-y-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Guest {i + 2}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <Label className="text-xs">Full name *</Label>
                          <Input
                            value={additionalGuests[i]?.name || ''}
                            onChange={e => setAdditionalGuests(prev => {
                              const updated = [...prev];
                              updated[i] = { ...updated[i], name: e.target.value };
                              return updated;
                            })}
                            placeholder={`Guest ${i + 2}'s name`}
                            className="mt-1 rounded-xl"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Email (optional)</Label>
                          <Input
                            type="email"
                            value={additionalGuests[i]?.email || ''}
                            onChange={e => setAdditionalGuests(prev => {
                              const updated = [...prev];
                              updated[i] = { ...updated[i], email: e.target.value };
                              return updated;
                            })}
                            placeholder="their@email.com"
                            className="mt-1 rounded-xl"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Phone (optional)</Label>
                          <Input
                            value={additionalGuests[i]?.phone || ''}
                            onChange={e => setAdditionalGuests(prev => {
                              const updated = [...prev];
                              updated[i] = { ...updated[i], phone: e.target.value };
                              return updated;
                            })}
                            placeholder="07xxx xxx xxx"
                            className="mt-1 rounded-xl"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Split payment */}
                <div className="mt-5 p-4 rounded-2xl border bg-card space-y-3">
                  <p className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>💳 Payment</p>
                  <div className="space-y-2">
                    <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${splitType === 'bulk' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/20'}`}>
                      <input type="radio" name="split" value="bulk" checked={splitType === 'bulk'} onChange={() => setSplitType('bulk')} className="mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">I'll pay for everyone</p>
                        <p className="text-xs text-muted-foreground">Full amount of £{totalPrice.toFixed(2)} — others owe £0</p>
                      </div>
                    </label>
                    <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${splitType === 'split' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/20'}`}>
                      <input type="radio" name="split" value="split" checked={splitType === 'split'} onChange={() => setSplitType('split')} className="mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Split equally</p>
                        <p className="text-xs text-muted-foreground">£{(totalPrice / groupSize).toFixed(2)} per person × {groupSize} people</p>
                      </div>
                    </label>
                  </div>
                  <div className="flex justify-between items-center pt-1 border-t border-border text-sm">
                    <span className="text-muted-foreground">You pay today</span>
                    <span className="font-semibold text-primary">£{splitAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Consultation Form (new clients only) */}
        {step === 2 && (
          <div className="fade-in">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardCheck size={24} className="text-primary" />
              <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Consultation Form</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">As a new client, please complete this quick health & preferences form</p>
            <ConsultationForm 
              clientName={clientName}
              clientEmail={clientEmail}
              compact={true}
              onComplete={() => {
                setConsultationDone(true);
                setStep(3);
                toast.success("Consultation form saved!");
              }}
            />
          </div>
        )}

        {/* Step 5: Confirm */}
        {step === 5 && (
          <>
          <BookingStep5Confirm
            selectedServices={selectedServices}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            totalDuration={totalDuration}
            totalPrice={totalPrice}
            newClientDiscount={newClientDiscount}
            clientName={clientName}
            clientEmail={clientEmail}
            addOnSelections={addOnSelections}
            getServiceTotal={getServiceTotal}
            voucherCode={voucherCode}
            setVoucherCode={setVoucherCode}
            voucherApplied={voucherApplied}
            setVoucherApplied={setVoucherApplied}
            voucherLoading={voucherLoading}
            setVoucherLoading={setVoucherLoading}
          />

          {/* Stripe deposit summary — shown when deposit is enabled */}
          {settings?.stripe_deposit_enabled && stripeDepositAmount > 0 && (
            <div className="mt-4 p-4 rounded-2xl border-2 flex items-start gap-3" style={{ borderColor: '#BFD8F5', background: '#E8F3FF' }}>
              <CreditCard size={18} className="shrink-0 mt-0.5" style={{ color: '#1F6FE5' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: '#1F6FE5' }}>
                  £{stripeDepositAmount.toFixed(2)} deposit to confirm — or pay £{Math.max(0, totalPrice - newClientDiscount - (voucherApplied?.amount_to_apply || 0)).toFixed(2)} in full now
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {settings?.deposit_type === 'percent'
                    ? `Deposit is ${settings?.deposit_amount}% of total. `
                    : 'Fixed deposit. '}
                  Both options charged securely via Stripe. Pay-in-full means no balance on the day.
                </p>
              </div>
            </div>
          )}
          </>
        )}



        {/* Step 6: Confirmation */}
        {step === 6 && booking && (
          <div className="fade-in text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Playfair Display' }}>Booking confirmed!</h2>
            <p className="text-muted-foreground mb-1">Booking ref: <strong>{booking.booking_id}</strong></p>
            <p className="text-sm text-muted-foreground mb-6">A confirmation has been sent to {clientEmail}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to={booking.manage_url}>
                <Button variant="secondary" className="rounded-xl">Manage booking</Button>
              </Link>
              <a href={`${API}/bookings/${booking.appointment?.manage_token}/ics`} download>
                <Button variant="outline" className="rounded-xl">Add to calendar</Button>
              </a>
              <Link to="/">
                <Button variant="ghost" className="rounded-xl">Back to home</Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Bottom sticky bar — hide on Consultation (2, self-contained) and Services (3, handled by SelectionPanel) */}
      {step < 6 && step !== 2 && step !== 3 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-border p-4 z-30">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div>
              {selectedServices.length > 0 && (
                <p className="text-sm">
                  {newClientDiscount > 0 && step >= 5 ? (
                    <>
                      <span className="font-semibold text-primary">£{Math.max(0, totalPrice - newClientDiscount).toFixed(2)}</span>
                      <span className="text-muted-foreground ml-1 line-through text-xs">£{totalPrice.toFixed(2)}</span>
                      <span className="text-green-600 ml-1 text-xs font-medium">-£5 new client</span>
                    </>
                  ) : (
                    <span className="font-semibold text-primary">£{totalPrice.toFixed(2)}</span>
                  )}
                  <span className="text-muted-foreground ml-1">· {totalDuration} min · {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''}</span>
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {step > 1 && (
                <Button variant="ghost" onClick={() => setStep(step - 1)} className="rounded-xl">
                  <ArrowLeft size={16} className="mr-1" /> Back
                </Button>
              )}
              {step === 4 && (
                <Button
                  data-testid="booking-wizard-next-button"
                  onClick={() => setStep(5)}
                  disabled={!selectedSlot}
                  className="rounded-xl"
                >
                  Next <ArrowRight size={16} className="ml-1" />
                </Button>
              )}
              {step === 1 && (
                <Button
                  data-testid="booking-wizard-next-button"
                  onClick={handleDetailsNext}
                  disabled={!clientName || !clientEmail || !policyAck}
                  className="rounded-xl"
                >
                  Next <ArrowRight size={16} className="ml-1" />
                </Button>
              )}
              {step === 5 && stripeDepositAmount > 0 ? (
                <div className="flex flex-col items-stretch sm:items-end gap-1.5 w-full sm:w-auto">
                  <Button
                    data-testid="booking-pay-deposit-button"
                    onClick={() => handleStripeBook("deposit")}
                    disabled={submitting}
                    className="rounded-xl w-full sm:w-auto"
                    style={{ background: 'linear-gradient(135deg,#1F6FE5 0%,#2DD4BF 100%)', border: 0, color: '#fff' }}
                  >
                    {submitting ? <><span className="mr-2 animate-spin">◌</span>Loading…</> : `Pay £${stripeDepositAmount.toFixed(2)} deposit`}
                  </Button>
                  <Button
                    data-testid="booking-pay-full-button"
                    onClick={() => handleStripeBook("full")}
                    disabled={submitting}
                    className="rounded-xl w-full sm:w-auto"
                    variant="outline"
                    style={{ borderColor: '#1F6FE5', color: '#1F6FE5' }}
                  >
                    Pay £{Math.max(0, totalPrice - newClientDiscount - (voucherApplied?.amount_to_apply || 0)).toFixed(2)} in full
                  </Button>
                </div>
              ) : step === 5 ? (
                <Button
                  data-testid="booking-confirm-button"
                  onClick={handleBook}
                  disabled={submitting}
                  className="rounded-xl"
                >
                  {submitting ? 'Booking...' : 'Confirm booking'}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* AI Assistant Chat */}
      {step < 6 && step !== 2 && (
        <>
          {/* Floating button for steps other than Details(1) & Services(3, has its own inline banner) */}
          {!chatOpen && step > 1 && step !== 3 && (
            <button 
              onClick={() => setChatOpen(true)}
              className="fixed bottom-20 right-4 z-40 w-12 h-12 rounded-2xl shadow-lg hover:-translate-y-0.5 transition-transform duration-200 overflow-hidden"
            >
              <img src="https://customer-assets.emergentagent.com/job_a2ffd257-50b9-4530-8932-b0e03417ee6f/artifacts/9v3936lg_Beyond%20AI.png" alt="Beyond AI" className="w-full h-full object-cover" />
            </button>
          )}
          
          {chatOpen && (
            <div className="fixed bottom-4 right-4 z-40 w-[340px] sm:w-[400px] bg-card border border-border rounded-2xl shadow-[0_10px_40px_rgba(43,18,27,0.18)] overflow-hidden fade-in" style={{ height: '480px', display: 'flex', flexDirection: 'column' }}>
              <div className="p-3 text-white flex items-center justify-between" style={{ background: 'linear-gradient(135deg,#1F6FE5 0%,#2DD4BF 100%)' }}>
                <div className="flex items-center gap-2">
                  <img src="https://customer-assets.emergentagent.com/job_a2ffd257-50b9-4530-8932-b0e03417ee6f/artifacts/9v3936lg_Beyond%20AI.png" alt="Beyond AI" className="w-8 h-8 rounded-xl object-cover" />
                  <div>
                    <p className="font-semibold text-sm">Beyond <span style={{ color: '#a5f3fc' }}>AI</span></p>
                    <p className="text-[10px] opacity-80">I know all our services &amp; prices</p>
                  </div>
                </div>
                <button onClick={() => setChatOpen(false)} className="hover:bg-white/20 rounded-lg p-1 transition-colors">
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {chatMessages.length === 0 && (
                  <>
                    <div className="chat-bubble-assistant p-3 max-w-[85%]">
                      <p className="text-sm">Hi lovely! I'm here to help you find the perfect treatment. What are you looking for today?</p>
                    </div>
                    <div className="space-y-1.5 pl-2">
                      {["What's best for weak/peeling nails?", "How long does a BIAB set take?", "What's the difference between gel and BIAB?"].map((q, i) => (
                        <button key={i} onClick={() => { setChatInput(q); }} className="block w-full text-left text-xs px-3 py-2 bg-secondary rounded-xl hover:bg-primary/10 transition-colors">
                          {q}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`p-3 max-w-[85%] text-sm ${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-assistant'}`}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="chat-bubble-assistant p-3 max-w-[85%]">
                    <div className="flex gap-1"><div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" /><div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{animationDelay: '0.1s'}} /><div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{animationDelay: '0.2s'}} /></div>
                  </div>
                )}
              </div>
              <div className="p-2 border-t border-border flex gap-2">
                <Input 
                  value={chatInput} 
                  onChange={e => setChatInput(e.target.value)} 
                  onKeyDown={e => e.key === 'Enter' && sendChat()}
                  placeholder="Ask me anything about our services..." 
                  className="rounded-xl text-sm" 
                />
                <Button size="icon" onClick={sendChat} disabled={chatLoading} className="rounded-xl shrink-0">
                  <Send size={16} />
                </Button>
              </div>
              <p className="text-[10px] text-center text-muted-foreground py-1">Beyond AI · only recommends from our real service menu</p>
            </div>
          )}
        </>
      )}

      {/* Spacer for sticky bottom */}
      {step < 6 && step !== 2 && step !== 3 && <div className="h-20" />}
      
      {/* Professional Accreditation - shown on the Services step */}
      {step === 3 && (
        <div className="max-w-3xl mx-auto px-4 py-8 border-t border-border mt-4 text-center">
          <div className="flex flex-col items-center gap-3 mb-3">
            <img src="https://customer-assets.emergentagent.com/job_salonos-book/artifacts/vuby3b20_ABT.png" alt="ABT Accredited" className="h-12 object-contain" />
            <span className="text-xs font-semibold tracking-wide" style={{ fontFamily: 'Playfair Display' }}>Professional Accreditation</span>
          </div>
          <p className="text-xs font-medium mb-2" style={{ fontFamily: 'Playfair Display' }}>ABT Accredited Nail Technician</p>
          <div className="space-y-1.5 text-[11px] text-muted-foreground">
            <p className="flex items-center justify-center gap-1.5">
              <svg className="w-3 h-3 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              Certified in Gel and Acrylic Nail Technology — Elizabeth Sands Beauty School
            </p>
            <p className="flex items-center justify-center gap-1.5">
              <svg className="w-3 h-3 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              Specialist training — TGB Academy — Nail Care During & After Chemotherapy
            </p>
          </div>
        </div>
      )}

      {/* Eligibility-block modal */}
      <Dialog open={!!eligibilityBlock} onOpenChange={(open) => { if (!open) setEligibilityBlock(null); }}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="eligibility-block-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
              <AlertTriangle size={18} className="text-amber-500" />
              Service not available for you
            </DialogTitle>
          </DialogHeader>
          {eligibilityBlock && (
            <div className="space-y-3 text-sm">
              {eligibilityBlock.isFirstTime ? (
                <p className="text-foreground">
                  <strong>{eligibilityBlock.blocked.map(s => s.name).join(', ')}</strong> is for returning clients only —
                  it requires existing nails from a previous appointment with us.
                </p>
              ) : (
                <p className="text-foreground">
                  Based on your last appointment <strong>{eligibilityBlock.daysSinceLast} day{eligibilityBlock.daysSinceLast !== 1 ? 's' : ''} ago</strong>,
                  this service isn't available right now.
                </p>
              )}
              {eligibilityBlock.blocked.map(s => {
                const min = s.eligibility_min_days_since_last;
                const max = s.eligibility_max_days_since_last;
                return (
                  <div key={s.id} className="text-xs bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="font-medium text-amber-900">{s.name}</p>
                    <p className="text-amber-700">
                      Available {min ? `from ${min} days` : ''}{(min && max) ? ' ' : ''}{max ? `up to ${max} days` : ''} after your last visit.
                    </p>
                  </div>
                );
              })}
              {eligibilityBlock.suggestions.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Try one of these instead</p>
                  <div className="space-y-1.5">
                    {eligibilityBlock.suggestions.map(s => (
                      <button
                        key={s.id}
                        data-testid={`eligibility-suggest-${s.id}`}
                        onClick={() => {
                          // Remove blocked services, add suggested one
                          const newSelection = selectedServices.filter(sel => !eligibilityBlock.blocked.find(b => b.id === sel.id));
                          newSelection.push(s);
                          setSelectedServices(newSelection);
                          setEligibilityBlock(null);
                          // Already on the Services step — no navigation needed
                        }}
                        className="w-full text-left px-3 py-2 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors flex items-center justify-between"
                      >
                        <div>
                          <p className="text-sm font-medium">{s.name}</p>
                          <p className="text-[10px] text-muted-foreground">{s.duration_minutes} min</p>
                        </div>
                        <span className="text-sm font-semibold text-primary">£{s.price.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              data-testid="eligibility-pick-other-btn"
              onClick={() => {
                if (!eligibilityBlock) return;
                // Remove blocked services — user is already on the Services step to pick again
                const newSelection = selectedServices.filter(sel => !eligibilityBlock.blocked.find(b => b.id === sel.id));
                setSelectedServices(newSelection);
                setEligibilityBlock(null);
              }}
              className="rounded-xl"
            >
              Choose another service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nail-length honesty check — hard-blocking confirmation when a
          client picks a shorter (cheaper) length than what was verified
          in person on their last visit. Buttons are stacked full-width and
          allowed to wrap (nail length names can be long, descriptive
          strings — e.g. "Long - Significant free edge or longer
          structure" — so a single-line nowrap row would overflow). */}
      <Dialog open={!!lengthMismatch} onOpenChange={(open) => { if (!open) setLengthMismatch(null); }}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="nail-length-mismatch-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
              <AlertTriangle size={18} className="text-amber-500 shrink-0" />
              Just double-checking
            </DialogTitle>
            <DialogDescription>
              We want to make sure your booking reflects your actual nail length.
            </DialogDescription>
          </DialogHeader>
          {lengthMismatch && (
            <p className="text-sm text-foreground break-words">
              Last visit, your nail length was recorded as <strong>{lengthMismatch.verifiedOpt.name}</strong>.
              You've selected <strong>{lengthMismatch.opt.name}</strong> this time — has your nail length changed,
              or would you like to update your selection?
            </p>
          )}
          <DialogFooter className="flex flex-col sm:flex-col gap-2 sm:justify-start sm:space-x-0">
            <Button
              data-testid="length-mismatch-confirm-btn"
              onClick={() => {
                applyLength(lengthMismatch.svc, lengthMismatch.opt);
                setLengthMismatch(null);
              }}
              className="rounded-xl w-full h-auto whitespace-normal text-left justify-start py-2.5"
            >
              Yes, it's changed — keep {lengthMismatch?.opt?.name.split(' - ')[0]}
            </Button>
            <Button
              variant="outline"
              data-testid="length-mismatch-update-btn"
              onClick={() => {
                applyLength(lengthMismatch.svc, lengthMismatch.verifiedOpt);
                setLengthMismatch(null);
              }}
              className="rounded-xl w-full h-auto whitespace-normal text-left justify-start py-2.5"
            >
              Use {lengthMismatch?.verifiedOpt?.name.split(' - ')[0]} instead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

